import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNationalSyncStore } from "../store/nationalSyncStore";
import { useAIContext } from "../store/aiContextStore";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";

/**
 * National Sync (v2)
 * 
 * UI for managing envelope generation, signature hashing,
 * ledger synchronization, and retry policies.
 */
export default function NationalSyncV2() {
  const { t } = useTranslation();
  const {
    envelopeSizeKB,
    signatureHash,
    ledgerHeight,
    ackStatus,
    retries,
    generateSignedEnvelope,
    sendToLedger,
    exportSyncEnvelope,
  } = useNationalSyncStore();

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Set active panel for AI context
  useEffect(() => {
    useAIContext.getState().setActivePanel("National Sync");
  }, []);

  // Update AI context inputs on change
  useEffect(() => {
    useAIContext.getState().setInput("envelopeSizeKB", envelopeSizeKB);
    useAIContext.getState().setInput("signatureHash", signatureHash);
    useAIContext.getState().setInput("ledgerHeight", ledgerHeight);
    useAIContext.getState().setInput("ackStatus", ackStatus);
    useAIContext.getState().setInput("retries", retries);
  }, [envelopeSizeKB, signatureHash, ledgerHeight, ackStatus, retries]);

  const retryPolicy = useNationalSyncStore((s) => s.getRetryPolicy());

  const handleGenerate = () => {
    setError(null);
    try {
      generateSignedEnvelope();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("nationalSync.generateFailed") || "Failed to generate envelope");
      setError(error);
      console.error("Generate envelope failed:", e);
    }
  };

  const handleSend = async () => {
    if (!signatureHash) {
      setError(new Error(t("nationalSync.noSignature") || "No signature hash available. Generate envelope first."));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await sendToLedger();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("nationalSync.sendFailed") || "Failed to send to ledger");
      setError(error);
      console.error("Send to ledger failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    setError(null);
    try {
      exportSyncEnvelope();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("nationalSync.exportFailed") || "Failed to export envelope");
      setError(error);
      console.error("Export failed:", e);
    }
  };

  return (
    <div className="space-y-6 font-orbitron bg-black text-neutral-100 p-6">
      <div className="font-orbitron text-evat-neon text-xl mb-4">
        {t("nationalSync.title") || "National Sync"}
      </div>

      <ErrorBanner error={error} onRetry={() => setError(null)} showRetry={!!error} />
      
      {loading && <LoadingSpinner size="md" text={t("nationalSync.sending") || "Sending to ledger..."} />}

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={handleGenerate}
          disabled={loading}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("nationalSync.generate") || "Generate Envelope"}
        </button>
        <button
          onClick={handleSend}
          disabled={!signatureHash || ackStatus === "pending" || loading}
          className="px-4 py-2 bg-evat-neon/20 border border-evat-neon text-evat-neon rounded hover:bg-evat-neon/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (t("nationalSync.sending") || "Sending...") : (t("nationalSync.send") || "Send to Ledger")}
        </button>
      </div>

      {/* Status Display */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-evat-gold/30 rounded p-4 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("nationalSync.envelopeSize") || "Envelope Size"}
          </div>
          <div className="text-lg font-orbitron text-evat-gold">
            {envelopeSizeKB} KB
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-4 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("nationalSync.ledgerHeight") || "Ledger Height"}
          </div>
          <div className="text-lg font-orbitron text-evat-neon">
            {ledgerHeight.toLocaleString()}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-4 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("nationalSync.ackStatus") || "Acknowledgment Status"}
          </div>
          <div
            className={`text-lg font-orbitron ${
              ackStatus === "accepted"
                ? "text-green-400"
                : ackStatus === "rejected"
                ? "text-red-400"
                : "text-yellow-400"
            }`}
          >
            {ackStatus.toUpperCase()}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-4 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("nationalSync.retries") || "Retries"}
          </div>
          <div className="text-lg font-orbitron text-evat-gold">
            {retries} / {retryPolicy.maxRetries}
          </div>
        </div>
      </div>

      {/* Signature Hash */}
      {signatureHash && (
        <div className="border border-evat-gold/30 rounded p-4 bg-black/50">
          <div className="text-xs text-neutral-400 mb-2">
            {t("nationalSync.signatureHash") || "Signature Hash"}
          </div>
          <div className="font-mono text-xs text-evat-neon break-all">
            {signatureHash}
          </div>
        </div>
      )}

      {/* Retry Policy */}
      <div className="border border-evat-gold/30 rounded p-4 bg-black/50">
        <div className="text-xs text-neutral-400 mb-2">
          {t("nationalSync.retryPolicy") || "Retry Policy"}
        </div>
        <div className="text-sm text-neutral-300">
          {t("nationalSync.nextRetry") || "Next retry delay"}:{" "}
          <span className="text-evat-gold">
            {retryPolicy.nextRetryMs}ms
          </span>
        </div>
      </div>

      {/* Export Button */}
      <div className="fixed bottom-4 right-4">
        <button
          onClick={handleExport}
          disabled={loading || !signatureHash}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t("nationalSync.export") || "Export JSON report"}
        >
          {t("nationalSync.export") || "Export JSON"}
        </button>
      </div>
    </div>
  );
}




